/**
 * Size a Workstream, and override its mix — Resource Plan RP-3.2 and RP-3.3, M3.
 *
 * Two tabs on one dialog, because they are two halves of the same decision: how big is
 * this component, and what is it made of.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY SIZING AND THE MIX ARE SEPARATE ACTIONS
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-3.3's last AC is about the NEXT resize — "Resizing after an override keeps the
 * overridden mix, not the template". Saving a mix therefore does NOT redistribute hours:
 * doing so would silently rewrite every cell in the Workstream, destroying months the
 * planner typed by hand and never asked to lose. The mix tab says so, and offers "size
 * again with this mix" as the explicit next step.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * THE THREE HOUR FIGURES
 * ──────────────────────────────────────────────────────────────────────────────
 * The dialog shows all three side by side, because the confusion between them is the
 * thing to get right:
 *
 *   Estimated   what the work was sized at before anyone staffed it. Edited in the
 *               Estimates panel; sizing never touches it.
 *   Template    what the last sizing distributed. RP-3.2's drift is this vs the grid.
 *   Entered     what is in the grid now.
 */

import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useSetWorkstreamMix,
  useSizeWorkstream,
  useWorkstreamTemplates,
} from "@/hooks/use-plan-composition";
import { useLaborCategories } from "@/hooks/use-rate-card";
import { canEditPlanContent } from "@/lib/plan-version";
import { cn } from "@/lib/utils";
import type { ResourcePlanDetailView, WorkstreamView } from "@/schemas/resource-plans";

type Tab = "size" | "mix";

const PRESETS = ["S", "M", "L", "XL"] as const;

/** Integer hundredths — never `Number()` on a percentage. */
function mixTotalBp(lines: readonly { mixPercent: string }[]): number {
  return lines.reduce((sum, l) => {
    const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(l.mixPercent.trim());
    if (match === null) return sum;
    return sum + Number(`${match[1] ?? "0"}${(match[2] ?? "").padEnd(2, "0")}`);
  }, 0);
}

export function WorkstreamSizingDialog({
  plan,
  workstream,
  open,
  onOpenChange,
}: {
  plan: ResourcePlanDetailView;
  workstream: WorkstreamView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: templates } = useWorkstreamTemplates(false, open);
  const { data: categories } = useLaborCategories();
  const size = useSizeWorkstream();
  const setMix = useSetWorkstreamMix();

  const [tab, setTab] = useState<Tab>("size");
  const [mode, setMode] = useState<"preset" | "hours">("preset");
  const [preset, setPreset] = useState<(typeof PRESETS)[number]>("M");
  const [hours, setHours] = useState(workstream.templateHours ?? "");
  const [replaceExisting, setReplaceExisting] = useState(false);

  const [mixDraft, setMixDraft] = useState(
    (workstream.mix ?? []).map((m) => ({
      laborCategoryId: m.laborCategoryId,
      laborCategoryName: m.laborCategoryName,
      mixPercent: m.mixPercent,
      templateMixPercent: m.templateMixPercent,
    })),
  );
  const [reason, setReason] = useState(workstream.mixOverrideReason ?? "");

  const workstreamId = workstream.id;
  if (workstreamId === null) return null;

  const template = (templates ?? []).find((t) => t.type === workstream.type);
  const overridden = workstream.mixOverrideReason !== null;
  const totalBp = mixTotalBp(mixDraft);
  const balanced = totalBp === 10_000;
  const steadyState = plan.entryMode === "SteadyState";

  const presetHours =
    template === undefined
      ? null
      : preset === "S"
        ? template.sizeSHours
        : preset === "M"
          ? template.sizeMHours
          : preset === "L"
            ? template.sizeLHours
            : template.sizeXlHours;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{workstream.name}</DialogTitle>
          <DialogDescription>
            {workstream.type === null ? "" : `${workstream.type} · `}
            {template === undefined
              ? "No active template for this type yet."
              : `Template v${template.version}`}
          </DialogDescription>
        </DialogHeader>

        {/* The three hour figures, side by side. */}
        <div className="grid grid-cols-3 gap-3 rounded-md bg-muted/40 p-3 text-sm">
          <div>
            <span className="block font-medium uppercase tracking-wide text-muted-foreground">
              Estimated
            </span>
            <span className="mt-0.5 block font-mono text-sm">
              {workstream.estimatedHours ?? "—"}
            </span>
          </div>
          <div>
            <span className="block font-medium uppercase tracking-wide text-muted-foreground">
              Template
            </span>
            <span className="mt-0.5 block font-mono text-sm">
              {workstream.templateHours ?? "—"}
            </span>
          </div>
          <div>
            <span className="block font-medium uppercase tracking-wide text-muted-foreground">
              Entered
            </span>
            <span className="mt-0.5 block font-mono text-sm">
              {/*
               * ENTERED, not effective. RP-3.2's drift is "did the planner change what
               * the template distributed", and a ramp is not such a change — showing the
               * effective figure here would contradict the drift percentage beside it.
               */}
              {workstream.enteredHours ?? workstream.totalHours}
              {workstream.templateDriftPercent !== null &&
                workstream.templateDriftPercent !== undefined &&
                workstream.templateDriftPercent !== "0.00" && (
                  <span className="ml-1.5 text-muted-foreground">
                    ({Number(workstream.templateDriftPercent) > 0 ? "+" : ""}
                    {workstream.templateDriftPercent}% vs template)
                  </span>
                )}
            </span>
          </div>
        </div>

        <nav className="flex border-b border-border text-sm">
          {(
            [
              ["size", "Size"],
              ["mix", overridden ? "Mix · overridden" : "Mix"],
            ] as [Tab, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                "px-3 py-1.5 font-medium transition-colors",
                tab === key
                  ? "border-b-2 border-foreground text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === "size" ? (
          <div className="space-y-4">
            {steadyState && (
              <p className="flex items-start gap-2 rounded-md bg-warning-100 p-2.5 text-sm text-warning-950 dark:text-warning-400">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                This plan is in Steady-State mode, where each Plan Line carries one hours-per-month
                figure. Switch it to month-by-month to size from a template.
              </p>
            )}
            {template === undefined && !steadyState && (
              <p className="flex items-start gap-2 rounded-md bg-warning-100 p-2.5 text-sm text-warning-950 dark:text-warning-400">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                There is no active Workstream Template for {workstream.type} yet. An admin can
                create one in Settings → Workstream Templates.
              </p>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-sm">Size by</Label>
                <Select
                  value={mode}
                  onValueChange={(v) => setMode(v === "hours" ? "hours" : "preset")}
                >
                  <SelectTrigger className="mt-1 h-8 w-36 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preset" className="text-sm">
                      A size preset
                    </SelectItem>
                    <SelectItem value="hours" className="text-sm">
                      Total hours
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {mode === "preset" ? (
                <div>
                  <Label className="text-sm">Preset</Label>
                  <div className="mt-1 flex gap-1">
                    {PRESETS.map((p) => (
                      <Button
                        key={p}
                        variant={preset === p ? "default" : "outline"}
                        size="sm"
                        className="h-8 w-12 px-0 text-sm"
                        onClick={() => setPreset(p)}
                      >
                        {p}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-sm">Total hours</Label>
                  <Input
                    inputMode="decimal"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="mt-1 h-8 w-32 text-sm"
                    placeholder="0.00"
                  />
                </div>
              )}

              {mode === "preset" && presetHours !== null && (
                <span className="pb-2 font-mono text-sm text-muted-foreground">
                  = {presetHours} hours
                </span>
              )}
            </div>

            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Start again from the template.</span>{" "}
                <span className="text-muted-foreground">
                  Removes Plan Lines this Workstream has that the mix does not mention, and the
                  hours typed on them. Leave unchecked to keep them and just move the mix&rsquo;s
                  own lines.
                </span>
              </span>
            </label>

            {(workstream.mix ?? []).length > 0 && (
              <div className="rounded-md bg-muted/40 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Will distribute across{" "}
                  {overridden ? "this Workstream's overridden mix" : "the template mix"}
                </p>
                <p className="mt-1 font-mono text-sm">
                  {(workstream.mix ?? [])
                    .map((m) => `${m.laborCategoryName} ${Number(m.mixPercent)}%`)
                    .join(", ")}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              An override applies to this Workstream only, and survives re-sizing. It does not move
              hours on its own — save it, then size again to redistribute.
            </p>

            <div className="flex items-baseline justify-between">
              <Label className="text-sm">Mix</Label>
              <span
                className={cn(
                  "font-mono text-sm font-semibold",
                  balanced ? "text-success-700 dark:text-success-400" : "text-error-700",
                )}
              >
                {(totalBp / 100).toFixed(2)}%{balanced ? "" : " — must be 100.00%"}
              </span>
            </div>

            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="pb-1 font-medium">Labor Category</th>
                  <th className="pb-1 text-right font-medium">Template</th>
                  <th className="pb-1 text-right font-medium">This Workstream</th>
                </tr>
              </thead>
              <tbody>
                {mixDraft.map((line, index) => (
                  <tr key={line.laborCategoryId} className="border-b border-border last:border-0">
                    <td className="py-1.5">{line.laborCategoryName}</td>
                    <td className="py-1.5 text-right font-mono text-muted-foreground">
                      {line.templateMixPercent === null
                        ? "—"
                        : `${Number(line.templateMixPercent)}%`}
                    </td>
                    <td className="py-1.5 text-right">
                      <Input
                        inputMode="decimal"
                        value={line.mixPercent}
                        onChange={(e) => {
                          const next = [...mixDraft];
                          next[index] = { ...line, mixPercent: e.target.value };
                          setMixDraft(next);
                        }}
                        className="ml-auto h-7 w-20 text-right text-sm"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <AddMixCategory
              existing={mixDraft.map((m) => m.laborCategoryId)}
              categories={(categories ?? []).filter((c) => c.active)}
              onAdd={(category) =>
                setMixDraft([
                  ...mixDraft,
                  {
                    laborCategoryId: category.id,
                    laborCategoryName: category.name,
                    mixPercent: "0.00",
                    templateMixPercent: null,
                  },
                ])
              }
            />

            <div>
              <Label className="text-sm">Why this Workstream differs</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="mt-1 text-sm"
                placeholder="Recorded in the plan's audit trail."
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {tab === "mix" && overridden ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={setMix.isPending}
              onClick={() =>
                setMix.mutate(
                  { workstreamId, lines: null },
                  { onSuccess: () => onOpenChange(false) },
                )
              }
            >
              Back to the template mix
            </Button>
          ) : (
            <span />
          )}

          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {tab === "size" ? (
              <Button
                size="sm"
                disabled={
                  !canEditPlanContent(plan) ||
                  size.isPending ||
                  steadyState ||
                  template === undefined ||
                  (mode === "hours" && hours.trim() === "")
                }
                onClick={() =>
                  size.mutate(
                    {
                      workstreamId,
                      ...(mode === "preset"
                        ? { sizePreset: preset }
                        : { totalHours: hours.trim() }),
                      replaceExisting,
                    },
                    { onSuccess: () => onOpenChange(false) },
                  )
                }
              >
                {size.isPending ? "Sizing…" : "Size this Workstream"}
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={
                  !canEditPlanContent(plan) ||
                  setMix.isPending ||
                  !balanced ||
                  reason.trim().length === 0
                }
                onClick={() =>
                  setMix.mutate(
                    {
                      workstreamId,
                      lines: mixDraft.map((m) => ({
                        laborCategoryId: m.laborCategoryId,
                        mixPercent: m.mixPercent,
                      })),
                      reason: reason.trim(),
                    },
                    { onSuccess: () => onOpenChange(false) },
                  )
                }
              >
                {setMix.isPending ? "Saving…" : "Save this mix"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMixCategory({
  existing,
  categories,
  onAdd,
}: {
  existing: readonly string[];
  categories: ReadonlyArray<{ id: string; name: string }>;
  onAdd: (category: { id: string; name: string }) => void;
}) {
  const used = new Set(existing);
  const available = categories.filter((c) => !used.has(c.id));
  if (available.length === 0) return null;

  return (
    <Select
      value=""
      onValueChange={(id) => {
        const found = available.find((c) => c.id === id);
        if (found !== undefined) onAdd(found);
      }}
    >
      <SelectTrigger className="h-7 w-56 text-sm">
        <SelectValue placeholder="Add a Labor Category" />
      </SelectTrigger>
      <SelectContent>
        {available.map((c) => (
          <SelectItem key={c.id} value={c.id} className="text-sm">
            {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
